import { millisecondsToMinutes } from 'date-fns';
import { Namespace, TFunction } from 'react-i18next';

import {
  isNotSelectAll,
  isValidOption,
} from 'components/forms/multiSelectField/MultiSelectField.constants';
import { BaseOption } from 'components/forms/multiSelectField/MultiSelectField.types';
import { GetAllLeadChannelsResponseTO, LeadChannel } from 'models/Leads';
import { Property, PropertyBusinessType } from 'models/Properties';
import {
  ConditionType,
  GetTriggerOptionsResponseTO,
  IsCompleted,
  IsSigned,
  RecipientType,
  TimeOperator,
  TimeType,
  TriggerActionType,
  TriggerCondition,
  TriggerConditionForDaysOfWeek,
  TriggerConditionForStay,
  TriggerEventType,
  TriggerTimeCondition,
} from 'models/automation/Triggers';
import {
  ALLOWED_CHANNELS_FOR_DIRECT_MESSAGE,
  ALLOWED_CHANNELS_FOR_POST_REVIEW,
} from '../TriggersTab.constants';
import { getRecipientUidAndTypeFromValue } from '../TriggersTab.utils';
import {
  MultiSelectOptionFormValue,
  TriggerConditionFormValue,
} from './TriggerAddEditModal.schema.utils';

export const buildCondition = (
  arg?: Partial<TriggerConditionFormValue>,
): TriggerConditionFormValue => {
  return {
    conditionType: '',
    all: undefined,
    backupTimeInMilliSeconds: undefined,
    channels: undefined,
    days: undefined,
    daysOfWeek: undefined,
    guests: undefined,
    operator: undefined,
    propertyUids: undefined,
    timeInMilliSeconds: undefined,
    timeType: undefined,
    ...arg,
  };
};

/**
 * Whether the direct message action is disabled based on the current allowed channels.
 */
export const isDirectMessageDisabled = (
  conditions?: TriggerConditionFormValue[],
) => {
  const channelCondition = conditions?.find(
    ({ conditionType }) => conditionType === ConditionType.CHANNEL,
  );

  if (!channelCondition || !channelCondition.channels?.length) {
    return true;
  }

  const areAllSelectedChannelsAllowed = channelCondition.channels?.every(
    (option: BaseOption) =>
      ALLOWED_CHANNELS_FOR_DIRECT_MESSAGE.includes(
        option?.value as LeadChannel,
      ),
  );

  return !areAllSelectedChannelsAllowed;
};

/**
 * Whether the create review action is disabled based on the current time condition.
 */
export const isCreateReviewDisabled = (
  timeCondition:
    | {
        condition?: TriggerConditionFormValue;
        type?: TriggerTimeCondition | '';
      }
    | undefined,
  otherConditions: TriggerConditionFormValue[] | undefined,
) => {
  const channelCondition = otherConditions?.find(
    ({ conditionType }) => conditionType === ConditionType.CHANNEL,
  );

  if (!channelCondition || !channelCondition.channels?.length) {
    return true;
  }

  if (timeCondition?.type !== TriggerTimeCondition.CHECK_OUT) {
    return true;
  }

  const areAllSelectedChannelsAllowed = channelCondition.channels?.every(
    (option: BaseOption) =>
      ALLOWED_CHANNELS_FOR_POST_REVIEW.includes(option?.value as LeadChannel),
  );

  return (
    timeCondition?.condition?.conditionType !== ConditionType.AFTER_STAY_ENDS ||
    !areAllSelectedChannelsAllowed
  );
};

/**
 * Whether the create job action is disabled based on the current event type.
 */
export const isCreateJobDisabled = (eventType?: TriggerEventType | '') => {
  return eventType !== TriggerEventType.NEW_BOOKING;
};

/**
 * Whether the SMS action is disabled based on the current trigger options.
 */
export const isSmsDisabled = (options?: GetTriggerOptionsResponseTO) => {
  return !options?.option.actions.includes(TriggerActionType.SMS);
};

/**
 * Whether the WhatsApp action is disabled based on the current trigger options.
 */
export const isWhatsAppDisabled = (options?: GetTriggerOptionsResponseTO) => {
  return !options?.option.actions.includes(TriggerActionType.WHATSAPP);
};

/**
 * Whether the 'COPY_AGENCY' recipient type is disabled.
 */
export const isCopyAgencyRecipientDisabled = (
  recipients: MultiSelectOptionFormValue[] | undefined,
) => {
  const selectedOptionsExceptCopyAgency = (recipients ?? [])
    ?.filter(isValidOption)
    .filter(isNotSelectAll)
    .filter((option) => {
      const { type } = getRecipientUidAndTypeFromValue(option.value);

      return type !== RecipientType.COPY_AGENCY;
    });

  return !selectedOptionsExceptCopyAgency.length;
};

/**
 * Given a time in milliseconds, it returns an object containing the `time` and `timeType`, used by the trigger add/edit
 * modal.
 */
export const getTimeAndType = (
  milliseconds: number,
): Pick<TriggerConditionFormValue, 'timeInMilliSeconds' | 'timeType'> => {
  const minutes = millisecondsToMinutes(milliseconds);

  // Days
  if (minutes % (1 * 24 * 60) === 0) {
    return { timeInMilliSeconds: minutes / 60 / 24, timeType: TimeType.DAYS };
  }

  // Hours
  if (minutes % (1 * 60) === 0) {
    return { timeInMilliSeconds: minutes / 60, timeType: TimeType.HOURS };
  }

  return { timeInMilliSeconds: minutes, timeType: TimeType.MINUTES };
};

export const isTimeCondition = (condition: {
  conditionType: ConditionType;
}): condition is TriggerConditionForStay | TriggerConditionForDaysOfWeek => {
  return [
    ConditionType.AFTER_STAY_ENDS,
    ConditionType.AFTER_STAY_STARTS,
    ConditionType.BEFORE_STAY_ENDS,
    ConditionType.BEFORE_STAY_STARTS,
    ConditionType.DAY_OF_WEEK,
  ].includes(condition.conditionType);
};

/**
 * Given the conditions stringified JSON sent by the BE, this converts it to an object compliant with the Trigger
 * Add/Edit modal form value.
 */
export const parseConditionsToFormValues = ({
  allLeadChannels,
  allProperties,
  condition,
  t,
}: {
  allLeadChannels: GetAllLeadChannelsResponseTO['leadChannels'] | undefined;
  allProperties: Property[] | undefined;
  condition: TriggerCondition | null;
  t: TFunction<Namespace<'en'>>;
}): {
  conditions: TriggerConditionFormValue[];
  timeCondition: {
    condition: TriggerConditionFormValue;
    type: TriggerTimeCondition | '';
  };
} => {
  const allPropertiesIncludingChildProperties = allProperties?.flatMap((p) => [
    // MU properties should be included when all is selected
    ...(p.businessType === PropertyBusinessType.HOTEL ? [] : [p]),
    ...(p.subUnits ?? []),
    ...(p.unitTypes ?? []),
  ]);

  const parsedCondition =
    condition ??
    ({
      conditionType: ConditionType.AND,
      conditions: [],
    } satisfies TriggerCondition);

  const { conditions } = parsedCondition;

  const timeCondition = conditions?.find(isTimeCondition);
  const otherConditions = conditions?.filter((c) => !isTimeCondition(c));

  const getTimeCondition = (
    currentCondition: TriggerConditionForStay | TriggerConditionForDaysOfWeek,
  ) => {
    switch (currentCondition.conditionType) {
      case ConditionType.DAY_OF_WEEK:
        return {
          condition: buildCondition({
            conditionType: ConditionType.DAY_OF_WEEK,
            daysOfWeek: currentCondition.daysOfWeek.map((weekDay) => ({
              label: t(`common.dayOfWeek.${weekDay}`),
              value: weekDay,
            })),
          }),
          type: TriggerTimeCondition.DAY_OF_WEEK,
        };

      default: {
        const nextCondition = {
          ...currentCondition,
          ...getTimeAndType(currentCondition.timeInMilliSeconds),
          operator: currentCondition.operator ?? TimeOperator.EQUAL,
        };

        if (typeof nextCondition.backupTimeInMilliSeconds === 'number') {
          nextCondition.backupTimeInMilliSeconds /= 1000 * 60 * 60;
        }

        return {
          condition: buildCondition(nextCondition),
          type:
            currentCondition.conditionType === ConditionType.AFTER_STAY_ENDS ||
            currentCondition.conditionType === ConditionType.BEFORE_STAY_ENDS
              ? TriggerTimeCondition.CHECK_OUT
              : TriggerTimeCondition.CHECK_IN,
        };
      }
    }
  };

  return {
    conditions:
      otherConditions?.map((currentCondition) => {
        switch (currentCondition.conditionType) {
          case ConditionType.GUESTS_NUMBER:
          case ConditionType.STAY_LENGTH:
            return buildCondition(currentCondition);

          case ConditionType.AGREEMENT_SIGNED:
            return buildCondition({
              conditionType: ConditionType.AGREEMENT_SIGNED,
              operator: IsSigned.IS_SIGNED,
            });

          case ConditionType.BOOKING_PAID_IN_FULL:
          case ConditionType.BOOKING_NOT_PAID_IN_FULL:
            return buildCondition({
              conditionType: ConditionType.BOOKING_PAID_IN_FULL,
              operator:
                currentCondition.conditionType ===
                ConditionType.BOOKING_PAID_IN_FULL
                  ? IsCompleted.IS_COMPLETED
                  : IsCompleted.IS_NOT_COMPLETED,
            });

          case ConditionType.CHANNEL:
            return buildCondition({
              all: currentCondition.all,
              channels: currentCondition.all
                ? allLeadChannels?.map((c) => ({
                    label: c.bestProfile,
                    value: c.channel,
                  })) ?? []
                : currentCondition.channels.map((channel) => ({
                    label:
                      allLeadChannels?.find((c) => c.channel === channel)
                        ?.bestProfile ?? '',
                    value: channel,
                  })),
              conditionType: currentCondition.conditionType,
            });

          case ConditionType.NEGATION:
            return buildCondition({
              conditionType: currentCondition.condition.conditionType,
              operator:
                currentCondition.condition.conditionType ===
                ConditionType.AGREEMENT_SIGNED
                  ? IsSigned.IS_NOT_SIGNED
                  : IsCompleted.IS_NOT_COMPLETED,
            });

          case ConditionType.PRE_ARRIVAL_FORM_SUBMITTED:
            return buildCondition({
              conditionType: ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
              operator: IsCompleted.IS_COMPLETED,
            });

          case ConditionType.PROPERTY:
            return buildCondition({
              all: currentCondition.all,
              conditionType: currentCondition.conditionType,
              propertyUids: currentCondition.all
                ? allPropertiesIncludingChildProperties?.map((p) => ({
                    label: p.name,
                    value: p.uid,
                  })) ?? []
                : currentCondition.propertyUids.map((propertyUid) => ({
                    label:
                      allPropertiesIncludingChildProperties?.find(
                        ({ uid }) => propertyUid === uid,
                      )?.name ?? '',
                    value: propertyUid,
                  })),
            });

          default:
            return buildCondition();
        }
      }) ?? [],
    timeCondition: timeCondition
      ? getTimeCondition(timeCondition)
      : {
          condition: buildCondition(),
          type: '',
        },
  };
};

/**
 * Returns the correct set of templates based on the TriggerActionType given the trigger options.
 */
export const getAvailableTemplates = ({
  actionType,
  triggerOptions,
}: {
  actionType: TriggerActionType;
  triggerOptions?: GetTriggerOptionsResponseTO;
}) => {
  switch (actionType) {
    case TriggerActionType.EMAIL:
    case TriggerActionType.DIRECT_MESSAGE:
      return triggerOptions?.option.emailTemplates ?? [];
    case TriggerActionType.SMS:
      return triggerOptions?.option.smsTemplates ?? [];
    case TriggerActionType.WHATSAPP:
      return triggerOptions?.option.whatsAppTemplates ?? [];
    default:
      return [];
  }
};
